import './ErrorPage.scss';
import { FC, ReactNode, useCallback, useContext } from 'react';
import { MantineContext, MantineProvider } from '@mantine/core';
import { MdContentCopy, MdHome, MdRefresh } from 'react-icons/md';
import Button from '../../atoms/Button';
import Accordion from '../../atoms/Accordion';
import Tooltip from '../../atoms/Tooltip';
import MascotBackground from '../MascotBackground';
import { cssVariablesResolver, mantineTheme } from '../../theme';

/** Совместимы с `FallbackProps` react-error-boundary: страницу можно отдать в `FallbackComponent`. */
export interface IErrorPageProps {
    /** Пойманная ошибка; в подробностях — её `message` или сама строка */
    error: unknown;
    /** «На главную страницу»: сброс boundary (переход решает его `onReset`) */
    resetErrorBoundary: (...args: unknown[]) => void;
    /** Белый логотип приложения (URL картинки) на брендовой панели */
    logoSrc: string;
    /** Метка рядом с логотипом (админка — «Admin») */
    logoBadge?: ReactNode;
}

// Как getErrorMessage из react-error-boundary.
const getErrorMessage = (error: unknown): string | undefined => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
        return error.message;
    }
    return undefined;
};

const ErrorPageContent: FC<IErrorPageProps> = ({ error, resetErrorBoundary, logoSrc, logoBadge }) => {
    const errorText = getErrorMessage(error) ?? '';

    const handleCopy = useCallback(() => {
        navigator.clipboard?.writeText(errorText).catch((e: unknown) => console.warn('Copy failed', e));
    }, [errorText]);

    return (
        <div className="rf-error-split">
            {/* Левая брендовая панель на всю высоту: градиент бренда + скаттер маскотов */}
            <aside className="rf-error-split__brand">
                <MascotBackground opacity={0.12} density={46000} color="#fff" stagger={45} duration={900} />

                <div className="rf-error-split__brand-logo">
                    <img src={logoSrc} className="rf-error-split__brand-logo-svg" alt="logo" />
                    {logoBadge}
                </div>

                <div className="rf-error-split__code">
                    <small>Ошибка</small>
                    500
                </div>
            </aside>

            {/* Правая колонка: заголовок, действия и детали ошибки (функциональность сохранена) */}
            <section className="rf-error-split__content">
                <div className="rf-error-split__content-inner">
                    <h1 className="rf-error-split__title">Что-то пошло не так</h1>
                    <p className="rf-error-split__subtitle">
                        В работе сервиса произошла непредвиденная ошибка. Попробуйте перезагрузить страницу.
                    </p>

                    <div className="rf-error-split__actions">
                        <Button
                            type="button"
                            variant="primary"
                            fullWidth
                            className="rf-error-split__btn rf-error-split__btn--primary"
                            onClick={() => {
                                resetErrorBoundary();
                            }}
                            startAdornment={<MdHome size="20" />}
                        >
                            На главную страницу
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            fullWidth
                            className="rf-error-split__btn rf-error-split__btn--ghost"
                            onClick={() => {
                                window.location.reload();
                            }}
                            startAdornment={<MdRefresh size="20" />}
                        >
                            Перезагрузить страницу
                        </Button>
                    </div>

                    <div className="rf-error-split__details">
                        <Accordion
                            hoverable={false}
                            data={[
                                {
                                    title: 'Подробности ошибки',
                                    defaultOpened: true,
                                    content: (
                                        <div className="rf-error-split__details-content">
                                            <p className="rf-error-split__error-text">{errorText}</p>
                                            {navigator.clipboard !== undefined ? (
                                                <div className="rf-error-split__copy">
                                                    <Tooltip
                                                        openDelay={100}
                                                        background="black"
                                                        position="bottom"
                                                        label="Копировать"
                                                    >
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            aria-label="Копировать текст ошибки"
                                                            startAdornment={<MdContentCopy size="16" />}
                                                            onClick={handleCopy}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            ) : null}
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

const currentColorScheme = () => {
    const scheme = document.documentElement.getAttribute('data-mantine-color-scheme');
    return scheme === 'dark' || scheme === 'light' ? scheme : undefined;
};

/**
 * Страница 500: брендовая панель с кодом ошибки, действия и подробности. Boundary приложения может стоять выше
 * корневого MantineProvider — при ошибке тот уходит вместе с упавшим деревом, а страница собрана из компонентов на
 * Mantine: без провайдера вместо неё был бы белый экран (EUPDEV-12917). Поэтому вне провайдера страница ставит свой,
 * с темой пакета и текущей цветовой схемой.
 */
const ErrorPage: FC<IErrorPageProps> = (props) => {
    const insideMantine = useContext(MantineContext) !== null;
    if (insideMantine) return <ErrorPageContent {...props} />;
    return (
        <MantineProvider
            theme={mantineTheme}
            cssVariablesResolver={cssVariablesResolver}
            forceColorScheme={currentColorScheme()}
        >
            <ErrorPageContent {...props} />
        </MantineProvider>
    );
};

export default ErrorPage;
